import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, FormFeedback, Button } from 'reactstrap';
import { toast } from 'react-toastify';
import { handleAddModifyOperation } from '../../../../Api/ApiMethods';
import PropTypes from 'prop-types';

const ArbitratorDataModal = ({ apiEndpoint, isOpen, toggle, mode, selectedArbitrator, refreshData }) => {
    const initialArbitratorData = {
        id: '',
        fullName: '',
        email: '',
    };

    const [arbitratorData, setArbitratorData] = useState(initialArbitratorData);
    const [errors, setErrors] = useState({});

    // Türkçe karakterleri İngilizce karşılıklarına çeviren helper fonksiyon
    const turkishToEnglish = (text) => {
        return text
            .replace(/İ/g, 'I')
            .replace(/ı/g, 'i')
            .replace(/Ğ/g, 'G')
            .replace(/ğ/g, 'g')
            .replace(/Ü/g, 'U')
            .replace(/ü/g, 'u')
            .replace(/Ş/g, 'S')
            .replace(/ş/g, 's')
            .replace(/Ö/g, 'O')
            .replace(/ö/g, 'o')
            .replace(/Ç/g, 'C')
            .replace(/ç/g, 'c')
            .toLowerCase();
    };

    useEffect(() => {
        if (isOpen) {
            if (mode === 'modify' && selectedArbitrator) {
                setArbitratorData({ ...initialArbitratorData, ...selectedArbitrator });
            } else {
                resetForm();
            }
            setErrors({});
        }
    }, [mode, selectedArbitrator, isOpen]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        let processedValue = value;

        if (name === 'fullName' && typeof processedValue === 'string') {
            processedValue = processedValue.replace(/\b\w/g, char => char.toLocaleUpperCase('tr-TR'))
                .replace(/\s+/g, ' '); // Sadece çoklu boşlukları tek boşluğa çevir, trim yapma
        } else if (name === 'email' && typeof processedValue === 'string') {
            // Email için Türkçe karakterleri İngilizce karşılıklarına çevir ve küçült
            processedValue = turkishToEnglish(processedValue);
        }

        setArbitratorData(prevData => ({ ...prevData, [name]: processedValue }));

        if (errors[name]) {
            setErrors(prevErrors => ({ ...prevErrors, [name]: '' }));
        }
    };

    const validateForm = () => {
        const validationErrors = {};

        if (!arbitratorData.fullName?.trim()) {
            validationErrors.fullName = 'Ad Soyad zorunludur.';
        } else if (arbitratorData.fullName.trim().length < 2) {
            validationErrors.fullName = 'Ad Soyad en az 2 karakter olmalıdır.';
        } else if (arbitratorData.fullName.trim().split(' ').filter(word => word.length > 0).length < 2) {
            validationErrors.fullName = 'En az iki kelime girmelisiniz.';
        }

        if (!arbitratorData.email?.trim()) {
            validationErrors.email = 'E-posta adresi zorunludur.';
        } else {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(arbitratorData.email.trim())) {
                validationErrors.email = 'Geçerli bir e-posta adresi giriniz.';
            }
        }

        setErrors(validationErrors);
        return Object.keys(validationErrors).length === 0;
    };

    const resetForm = () => {
        setArbitratorData(initialArbitratorData);
        setErrors({});
    };

    const handleCancel = () => {
        resetForm();
        toggle();
    };

    const handleOperation = async () => {
        if (!validateForm()) {
            toast.error('Lütfen formdaki hataları düzeltin.');
            const firstErrorKey = Object.keys(errors).find(key => errors[key]);
            if (firstErrorKey) document.getElementById(firstErrorKey)?.focus();
            return;
        }

        const payload = {
            fullName: arbitratorData.fullName.trim().toLocaleUpperCase('tr-TR'),
            email: arbitratorData.email.trim(),
            ...(mode === 'modify' && selectedArbitrator?.id && { id: selectedArbitrator.id })
        };

        console.log("Sending Payload:", payload);

        try {
            await handleAddModifyOperation({
                apiEndpoint,
                selectedItem: mode === 'modify' ? { id: selectedArbitrator?.id } : null,
                mode,
                data: payload,
                refreshDataCallback: refreshData,
                componentName: 'Hakem',
            });
            toggle();
        } catch (error) {
            console.error("Hakem operasyonu başarısız:", error);
        }
    };

    const getModalTitle = () => {
        return mode === 'add' ? 'Hakem Ekle' : 'Hakem Düzenle';
    };

    const getButtonLabel = () => {
        return mode === 'add' ? 'Ekle' : 'Güncelle';
    };

    return (
        <Modal isOpen={isOpen} toggle={handleCancel} backdrop="static">
            <ModalHeader toggle={handleCancel}>{getModalTitle()}</ModalHeader>
            <ModalBody>
                <Form>
                    <FormGroup>
                        <Label for="fullName">Ad Soyad <span className="text-danger">*</span></Label>
                        <Input
                            type="text"
                            id="fullName"
                            name="fullName"
                            value={arbitratorData.fullName || ''}
                            onChange={handleInputChange}
                            placeholder="Hakem Ad Soyad"
                            invalid={!!errors.fullName}
                        />
                        <FormFeedback>{errors.fullName}</FormFeedback>
                    </FormGroup>
                    <FormGroup>
                        <Label for="email">E-posta <span className="text-danger">*</span></Label>
                        <Input
                            type="email"
                            id="email"
                            name="email"
                            value={arbitratorData.email || ''}
                            onChange={handleInputChange}
                            placeholder="hakem@example.com"
                            invalid={!!errors.email}
                        />
                        <FormFeedback>{errors.email}</FormFeedback>
                    </FormGroup>
                </Form>
            </ModalBody>
            <ModalFooter>
                <Button color="primary" onClick={handleOperation}>
                    {getButtonLabel()}
                </Button>
                <Button color="secondary" onClick={handleCancel} className="ms-2">
                    İptal
                </Button>
            </ModalFooter>
        </Modal>
    );
};

ArbitratorDataModal.propTypes = {
    apiEndpoint: PropTypes.string.isRequired,
    isOpen: PropTypes.bool.isRequired,
    toggle: PropTypes.func.isRequired,
    mode: PropTypes.oneOf(['add', 'modify']).isRequired,
    selectedArbitrator: PropTypes.object,
    refreshData: PropTypes.func.isRequired,
};

export default ArbitratorDataModal;
